import type { FC, PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useRef } from 'react';

import type { Focus } from '../FocusPlaceholder';
import FocusPlaceholder from '../FocusPlaceholder';

import type { FocusCatcherProps } from './FocusCatcher.types';
import { containsValidFocus } from './containsValidFocus';

const FocusCatcher: FC<PropsWithChildren<FocusCatcherProps>> = ({ state, fullHeight = false, children }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const containsFocusRef = useRef<boolean>(false);
  const focusPlaceholderRef = useRef<Focus>();

  const onBlur = useCallback(() => {
    containsFocusRef.current = false;
  }, []);

  const onFocus = useCallback(() => {
    containsFocusRef.current = true;
  }, []);

  const setPlaceholderFocus = useCallback((focus: Focus) => {
    focusPlaceholderRef.current = focus;
  }, []);

  useEffect(() => {
    if (containsFocusRef.current) {
      // If focus is somewhere valid, assume it was put there intentionally - eg.
      // by some component focusing a child as it mounts - and leave it where it is.
      const focusIsValid = containsValidFocus(rootRef.current, document.activeElement);
      if (!focusIsValid && focusPlaceholderRef.current) {
        focusPlaceholderRef.current();

        // We may not get an onBlur event (quirk of how React handles some types of events)
        // as a result of moving focus to the placeholder; so mark this as false explicitly.
        containsFocusRef.current = false;
      }
    }
  }, [state]);

  const styles = fullHeight ? { height: '100%' } : undefined;

  return (
    <>
      <FocusPlaceholder focusRef={setPlaceholderFocus} />
      <div onBlur={onBlur} onFocus={onFocus} ref={rootRef} style={styles} data-testid="focus-catcher-wrapper">
        {children}
      </div>
    </>
  );
};

export default FocusCatcher;
